import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  ChatPannel,
  FlexCol,
  FlexRow,
  HR,
  Label,
  MultiImageSlider,
  PropertyDetailMap,
  PropertyDetailStreetView,
  SchoolTable,
  LoadingView,
  NotificationComponent,
  Button,
  PropertyInfoActions,
} from '_app/components';
import NotFound from '../pages/NotFound';
import { FullScreenImageModal, RecentActivitiesModal } from '_app/components/modals';

import Utils from '_app/utils';
import { ReactComponent as LIKE_ICON } from '_app/assets/images/like.svg';
import { ReactComponent as DISLIKE_ICON } from '_app/assets/images/dislike.svg';

import moment from 'moment-timezone';
import { hasNewPrice, isManuallyAdded, isNewProperty, isComingSoon, isPrivatePerSeller } from '_app/utils/propertyParser';
import { usePropertyDetailController } from '_app/features/property-detail/usePropertyDetailController';

const PropertyDetail = () => {
  const params = useParams();
  const controller = usePropertyDetailController(params.propertyId);
  const [showFullImage, setShowFullImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const item = controller.property;
  const loading = controller.loading;
  const events = controller.upcomingEvents;

  const isStaff = controller.user && !!controller.user.staff_session;

  if (!item && !loading) {
    return null;
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      {!!item && (
        <FlexCol className="bg-white w-full flex-1 items-center pb-20 overflow-y-auto relative">
          <MultiImageSlider
            className="w-full z-10"
            images={item.photos}
            onClick={(selectedImage) => {
              setSelectedImage(selectedImage || 0);
              setShowFullImage(true);
            }}
          />
          <FlexRow className="absolute top-2 left-2 sm:top-4 sm:left-4 items-center z-20">
            {isNewProperty(item) && (
              <Label
                className="bg-yellow-600 shadow-inner text-white rounded-full mx-1 px-6 pt-0.5 pb-0 font-primary-bold text-sm sm:text-lg"
                label="New"
              />
            )}
            {hasNewPrice(item) && (
              <Label
                className="bg-yellow-400 shadow-inner text-white rounded-full mx-1 px-6 pt-0.5 pb-0 font-primary-bold text-sm sm:text-lg"
                label="New Price"
              />
            )}
            {isManuallyAdded(item) && (
              <Label
                className="bg-purple-600 shadow-inner text-white rounded-full mx-1 px-6 pt-0.5 pb-0 font-primary-bold text-sm sm:text-lg"
                label="Added"
              />
            )}
            {isComingSoon(item) && (
              <Label
                className="bg-red-600 shadow-inner text-white rounded-full mx-1 px-6 pt-0.5 pb-0 font-primary-bold text-sm sm:text-lg"
                label="Coming Soon"
              />
            )}
            {isPrivatePerSeller(item) && (
              <Label
                className="bg-red-600 shadow-inner text-white rounded-full mx-1 px-6 pt-0.5 pb-0 font-primary-bold text-sm sm:text-lg"
                label="Private Per Seller"
              />
            )}
          </FlexRow>
          <FlexCol className="w-full md:max-w-3xl lg:max-w-4xl z-20 relative px-3 sm:px-3 md:px-0">
            <FlexRow className="absolute -top-12">
              <FlexCol className="bg-black bg-opacity-80 text-white text-3xl font-primary h-12 px-3 inline-block align-middle w-auto justify-center pr-8">
                <span>{`$${item.price.toLocaleString()}`}</span>
              </FlexCol>
            </FlexRow>
            <FlexRow className="absolute bg-black bg-opacity-80 -top-12 right-3 md:right-0 border-primary2">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  controller.propertyActions.onLike(item);
                }}
                className={`cursor-pointer flex justify-center items-center w-12 h-12 text-white transition duration-500 ease-in-out hover:bg-primary-red hover:bg-opacity-80 ${
                  item.rating === 1 ? 'bg-primary-red bg-opacity-70' : 'bg-black bg-opacity-40'
                }`}
              >
                <LIKE_ICON width={32} height={32} fill="white" />
              </div>
              <div className="w-px h-12 bg-white bg-opacity-20" />
              <div
                className={`cursor-pointer flex justify-center items-center w-12 h-12  hover:bg-primary2 text-white ${
                  item.rating === -1 ? 'bg-primary2' : 'bg-black bg-opacity-40'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  controller.propertyActions.onDisLike(item);
                }}
              >
                <DISLIKE_ICON width={24} fill="white" />
              </div>
            </FlexRow>
            <FlexRow className="px-3 my-4 justify-around">
              <PropertyInfoActions
                mlsnum={item.mlsnum}
                user={controller.user}
                tourRequestState={controller.propertyActions.tourRequestState}
                moreInfoRequestState={controller.propertyActions.moreInfoRequestState}
                onRequestTour={controller.propertyActions.onRequestTour}
                onRequestMoreInfo={controller.propertyActions.onRequestMoreInfo}
                onPreApproval={controller.propertyActions.onPreApproval}
              />
            </FlexRow>
            <HR />
            <FlexCol className="py-8 font-primary-bold text-primary2 text-xl">
              <div className="">{item.address}</div>
              <div className="">
                {item.city}, {item.zip}
                <Link to="#" className="mr-2 ml-4 font-primary-bold text-primary2 text-xl">
                  Directions
                </Link>
                <span className="text-primary2">{'●'}</span>
                <Link to="#" className="ml-2 font-primary-bold text-xl text-primary5" onClick={() => controller.activities.onOpen()}>
                  Recent Activities
                </Link>
              </div>
            </FlexCol>
            <FlexRow className="p-4 border bg-gray-50">
              <FlexCol className="flex-1">
                {!!item.bedrooms && (
                  <FlexRow>
                    <Label label={item.bedrooms} className="mr-2 font-primary-bold text-base leading-5" />
                    <Label label={`Bed${item.bedrooms > 1 ? 's' : ''}`} className="font-primary text-sm pt-px" />
                  </FlexRow>
                )}
                {!!item.full_bathrooms && (
                  <FlexRow>
                    <Label label={item.full_bathrooms} className="mr-2 font-primary-bold text-base leading-5" />
                    <Label label={`Full Bath${item.full_bathrooms > 1 ? 's' : ''}`} className="font-primary text-sm pt-px" />
                  </FlexRow>
                )}
                {!!item.half_bathrooms && (
                  <FlexRow>
                    <Label label={item.half_bathrooms} className="mr-2 font-primary-bold text-base leading-5" />
                    <Label label={`Half bath${item.half_bathrooms > 1 ? 's' : ''}`} className="font-primary text-sm pt-px" />
                  </FlexRow>
                )}
              </FlexCol>
              <FlexCol className="flex-1">
                {!!item.building_size && (
                  <FlexRow>
                    <Label label={item.building_size.toLocaleString()} className="mr-2 font-primary-bold text-base leading-5" />
                    <Label label="Building Sqft." className="font-primary text-sm pt-px" />
                  </FlexRow>
                )}
                {!!item.lot_size && (
                  <FlexRow>
                    <Label label={item.lot_size.toLocaleString()} className="mr-2 font-primary-bold text-base leading-5" />
                    <Label label="Lot Sqft." className="font-primary text-sm pt-px" />
                  </FlexRow>
                )}
                <FlexRow>
                  <Label label={item.property_type} className="mr-2 font-primary text-sm pt-px leading-5" />
                </FlexRow>
              </FlexCol>
            </FlexRow>
            {!!events.length && (
              <FlexCol className="mt-12 mb-4">
                <Label label="Upcoming Open Houses" className="font-primary-bold text-primary2 text-xl mb-2" />
                {events.map((event, index) => (
                  <FlexCol key={index}>
                    <Label
                      className="text-primary2 mb-1"
                      label={`${moment(event.datetime_start).tz('America/Chicago').format('dddd, MMM DD')} (${moment(event.datetime_start)
                        .tz('America/Chicago')
                        .format('hh:mm A')} - ${moment(event.datetime_end).tz('America/Chicago').format('hh:mm A')})`}
                    />
                  </FlexCol>
                ))}
                {events[0] && events[0].show_reserve_spot && (
                  <FlexRow className="mt-2">
                    <Label label="Would you like to attend this open house?" className="text-base sm:text-lg text-primary4 mb-1" />
                    <Button
                      label="Yes"
                      className="bg-primary5 border border-primary4 text-primary4 px-5 ml-2 py-0"
                      onClick={() => controller.onReserveOpenHouse(events[0])}
                    />
                  </FlexRow>
                )}
              </FlexCol>
            )}
            <FlexCol className="mt-8 mb-4 h-96">
              <Label label="View Your Neighbors" className="font-primary-bold text-primary2 text-xl my-2" />
              <PropertyDetailMap item={item} />
            </FlexCol>
            <FlexCol className="my-4 h-96">
              <Label label="Street View" className="font-primary-bold text-primary2 text-xl my-2" />
              <PropertyDetailStreetView item={item} />
            </FlexCol>
            <HR />
            <div className="mt-12">
              <Label label="General Description" className="font-primary-bold text-primary2 text-xl" />
              <Label label={`${isStaff ? `${item.mlsnum} ` : ''} ${item.public_description}`} className="text-base leading-8 text-gray-600" />
            </div>
            {Utils.generatePropertyGeneralRows(item).map(({ value, label }, index) => (
              <div key={index} className="">
                <HR className="my-3" />
                <FlexRow>
                  <Label label={label} className="font-primary-bold text-base flex-1" />
                  <Label label={value} className="text-base flex-1 font-primary" />
                </FlexRow>
              </div>
            ))}
            <div className="mt-12">
              <Label label="Taxes & Fees" className="font-primary-bold text-primary2 text-xl" />
            </div>
            {Utils.generatePropertyTaxRows(item).map(({ value, label, isLink }, index) => (
              <div key={index} className="">
                {!isLink && <HR className="my-3" />}
                <FlexRow className={isLink ? 'justify-center mt-4' : ''}>
                  {isLink && (
                    <a href={value} rel="noreferrer" target="_blank">
                      <Label label={label} className="font-primary-bold text-base flex-1 text-primary5" />
                    </a>
                  )}
                  {!isLink && (
                    <>
                      <Label label={label} className="font-primary-bold text-base flex-1" />
                      <Label label={value} className="text-base flex-1" />
                    </>
                  )}
                </FlexRow>
              </div>
            ))}
            <div className="mt-12">
              <Label label="Interior Features" className="font-primary-bold text-primary2 text-xl" />
            </div>
            {Utils.generatePropertyInteriorRows(item).map(({ value, label }, index) => (
              <div key={index} className="">
                <HR className="my-3" />
                <FlexRow>
                  <Label label={label} className="font-primary-bold text-base flex-1" />
                  <Label label={value} className="text-base flex-1" />
                </FlexRow>
              </div>
            ))}
            <div className="mt-12">
              <Label label="Exterior Features" className="font-primary-bold text-primary2 text-xl" />
            </div>
            {Utils.generatePropertyExteriorRows(item).map(({ value, label }, index) => (
              <div key={index} className="">
                <HR className="my-3" />
                <FlexRow>
                  <Label label={label} className="font-primary-bold text-base flex-1" />
                  <Label label={value} className="text-base flex-1" />
                </FlexRow>
              </div>
            ))}
            <SchoolTable property={item} />
            <HR />
          </FlexCol>
        </FlexCol>
      )}
      {showFullImage && (
        <FullScreenImageModal visible={showFullImage} slideIndex={selectedImage} images={item.photos} onClose={() => setShowFullImage(false)} />
      )}
      <RecentActivitiesModal
        visible={controller.activities.visible}
        items={controller.activities.items}
        loading={controller.activities.loading}
        onClose={controller.activities.onClose}
      />
      <ChatPannel {...controller.chat} />
      {loading && <LoadingView />}
    </div>
  );
};

export default PropertyDetail;
